import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, TextField } from "stories/Admin";
import useFetch from "hooks/useFetch";

interface MosqueRef {
  orgNr: string;
  name: string | null;
}

interface MosqueReplacement {
  orgNr: string;
  replaces: MosqueRef | null;
  replacedBy: MosqueRef[];
}

interface MosqueOption {
  orgNr: string;
  name?: string | null;
  orgName?: string | null;
  city?: string | null;
}

interface ReplacesMosqueFieldProps {
  /** Moskeen som redigeres (den NYE moskeen når feltet settes). */
  orgNr: string;
  /** Alle moskeer (fra siden), å velge den gamle blant. */
  mosques: MosqueOption[];
}

const MAX_OPTIONS = 8;

const label = (m: { orgNr: string; name?: string | null; orgName?: string | null }) =>
  `${m.name || m.orgName || m.orgNr} · ${m.orgNr}`;

/**
 * «Erstatter moské» (superadmin): settes på den NYE moskeen. Push fra den sendes også til følgerne av den gamle
 * (én melding til begge), og vises i innboksen og meldingslista deres – så ingen går glipp av noe mens folk bytter.
 */
const ReplacesMosqueField: React.FC<ReplacesMosqueFieldProps> = ({ orgNr, mosques }) => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const [state, setState] = useState<MosqueReplacement | null>(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    let active = true;
    setState(null);
    setSearch("");
    setError("");
    setSaved("");
    executeRef.current(`/api/v1/mosques/${encodeURIComponent(orgNr)}/replacement`, "GET").then((result) => {
      if (active && result) setState(result as MosqueReplacement);
    });
    return () => {
      active = false;
    };
  }, [orgNr]);

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return mosques
      .filter((m) => m.orgNr !== orgNr)
      .filter((m) => [m.name, m.orgName, m.orgNr, m.city].filter(Boolean).join(" ").toLowerCase().includes(q))
      .slice(0, MAX_OPTIONS);
  }, [mosques, search, orgNr]);

  const save = async (replacesOrgNr: string | null) => {
    setBusy(true);
    setError("");
    setSaved("");
    const result = (await execute(`/api/v1/mosques/${encodeURIComponent(orgNr)}/replacement`, "PUT", {
      replacesOrgNr,
    })) as MosqueReplacement | null;
    setBusy(false);
    if (!result) {
      setError(lastError()?.message || "Kunne ikke lagre.");
      return;
    }
    setState(result);
    setSearch("");
    setSaved(replacesOrgNr ? "Lagret. Push fra denne moskeen går nå også til følgerne av den gamle." : "Fjernet.");
  };

  return (
    <div aria-label="Erstatter moské">
      <h4 style={{ margin: "16px 0 4px" }}>Erstatter moské</h4>
      <p style={{ margin: "0 0 8px", fontSize: 13 }}>
        Settes på den <b>nye</b> moskeen når en moské har fått nytt org.nr. Push herfra sendes også til følgerne av den
        gamle (de som følger begge, får den én gang) og vises i innboksen deres.
      </p>
      {state?.replaces ? (
        <p style={{ margin: "0 0 8px" }}>
          Erstatter <Badge tone="gold">{label(state.replaces)}</Badge>{" "}
          <Button size="sm" variant="secondary" loading={busy} onClick={() => save(null)}>
            Fjern
          </Button>
        </p>
      ) : (
        <>
          <TextField
            label="Gammel moské (søk etter navn eller org.nr.)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="F.eks. 999304613_1"
          />
          {options.length > 0 && (
            <ul style={{ listStyle: "none", padding: 0, margin: "6px 0" }}>
              {options.map((m) => (
                <li key={m.orgNr} style={{ marginBottom: 4 }}>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => save(m.orgNr)}>
                    Erstatter {label(m)}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {state && state.replacedBy.length > 0 && (
        <p style={{ margin: "4px 0", fontSize: 13 }}>
          Erstattes av: {state.replacedBy.map((m) => label(m)).join(", ")} (push derfra går også til følgerne av denne).
        </p>
      )}
      {error && <Alert tone="error">{error}</Alert>}
      {saved && <Alert tone="ok">{saved}</Alert>}
    </div>
  );
};

export default ReplacesMosqueField;
